import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";

import { PageHeader } from "@/app/PageHeader";
import { RenameButton } from "@/components/RenameButton";
import { hasLoadedOnce, useAsync } from "@/lib/useAsync";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { useObservationClock } from "@/lib/useObservationClock";
import { useDocumentVisible } from "@/lib/useDocumentVisible";
import { useWorkspaceStream } from "@/lib/useWorkspaceStream";
import { useModalA11y } from "@/lib/useModalA11y";
import { useVerifiedPerformanceMilestone } from "@/lib/usePerformanceMilestone";
import { performanceEnabled } from "@/lib/performanceDiagnostics";
import type { CurrentReadingProof } from "@/features/widgets/currentReadingReadiness";
import {
  formatRelative,
  getDeviceDisplayName,
  getFunctions,
  getSignalMetadata,
  isDeviceOnlineNow,
  normalizeMode,
  parseMetaJson,
} from "@/features/devices/adapters";
import { ApiError } from "@/lib/http";
import { useAuth } from "@/features/auth/AuthContext";
import {
  claimDevice,
  provisionarCredencial,
  type CredencialDeDispositivo,
} from "@/features/devices/api";
import { ErrorState } from "@/features/devices/components/ErrorState";
import { LoadingState } from "@/features/devices/components/LoadingState";
import {
  WidgetEditorModal,
  type AvailableSource,
} from "@/features/widgets/components/WidgetEditorModal";
import {
  buildStarterWidgets,
  WidgetPanel,
  type StarterSignalDefinition,
} from "@/features/widgets/components/WidgetPanel";
import {
  resolveStaleAfterMs,
  resolveWidgetValue,
  type SlotRuntime,
} from "@/features/widgets/selectors";
import type { DashboardWidget } from "@/features/widgets/types";
import {
  applyWidgetWindow,
  deriveChartWidget,
  revertChartWidget,
} from "@/features/widgets/chartWidgets";
import { mesclarWidgets } from "@/features/widgets/mergeWidgets";
import { useDashboardWidgets } from "@/features/widgets/useDashboardWidgets";
import { useWidgetWindows } from "@/features/widgets/useWidgetWindows";
import { useSignalConfigs } from "@/features/signals/useSignalConfigs";
import { joinFunctionAndValueLabel } from "@/features/signals/signalName";
import { knownSectionNames } from "@/features/widgets/sections";
import {
  widgetDefinesSignalIdentity,
  widgetToSignalConfig,
} from "@/features/signals/signalConfigFromWidget";
import type { ParsedMeta } from "@/features/devices/types";
import { setorEhConhecido } from "@/features/workspaces/setorConhecido";
import { useWorkspaces } from "@/features/workspaces/useWorkspaces";
import { DEFAULT_OPERATOR_USER_ID } from "@/features/users/localUser";
import {
  getMachineCalcStorageKey,
  loadLocalCalcFields,
} from "@/features/machines/localCalculations";
import type { CalcField } from "@/features/machines/types";
import { IconBroadcast, IconClock, IconPlus, IconRefresh } from "@/lib/icons";

import { getMachineDashboard } from "../api";
import {
  buildStarterSignalsForDashboard,
  deriveSignalStructureVersion,
  sortMetaFunctionsByPort,
} from "../composition";
import { useConfigChangeNotice } from "../useConfigChangeNotice";
import { isOperatorSelectableSignal } from "../widgetCatalog";
import { resolveSignalKind } from "@ghm/mqtt-contract";

type Mode = "view" | "edit";
import { parseShiftView, SHIFT_VIEW_PARAM } from "@/features/shifts/shiftUrlState";
const ShiftDashboard = lazy(() => import("@/features/shifts/ShiftDashboard"));

/**
 * Lê `cadence.telemetryMs` do meta parseado, com guardas — o device declara
 * a cadência e a janela de frescor do widget deriva dela (P0.1b). Retorna
 * undefined quando ausente/inválido, caindo no default em `resolveStaleAfterMs`.
 */
function readTelemetryMs(meta: ParsedMeta | null): number | undefined {
  const cadence = meta?.cadence;
  if (cadence && typeof cadence === "object") {
    const ms = (cadence as { telemetryMs?: unknown }).telemetryMs;
    if (typeof ms === "number" && Number.isFinite(ms) && ms > 0) return ms;
  }
  return undefined;
}

/**
 * Tela operacional do workflow.
 *
 * Layout do design `dashboard-operacional`:
 * - PageHeader (breadcrumb + título com badge + meta + ações)
 * - Section "Widgets operacionais"
 * Fórmulas manuais ficam fora desta tela por enquanto; voltam depois como
 * área avançada, separadas dos widgets operacionais prontos.
 */
export function MachineDashboardPage() {
  const params = useParams();
  // Estado de editor, janelas e layout pertence ao workflow. Nem um modal ou
  // rascunho aberto em A pode atravessar a navegacao para B.
  return <MachineDashboardContent key={`${params.workflowId ?? params.workspaceId}/${params.machineId ?? "main"}`} />;
}

function MachineDashboardContent() {
  // Tolerante a dois formatos de rota:
  //  - antigo: /workspaces/:workspaceId/machines/:machineId
  //  - novo : /workflows/:workflowId
  const params = useParams<{
    workspaceId?: string;
    machineId?: string;
    workflowId?: string;
  }>();
  const rawWorkspaceId = params.workflowId ?? params.workspaceId ?? "";
  const rawMachineId = params.machineId ?? "main";
  const decodedWorkspaceId = decodeURIComponent(rawWorkspaceId);
  const decodedMachineId = decodeURIComponent(rawMachineId);
  const { permissoes } = useAuth();
  const location = useLocation();
  const [currentReadingProof, setCurrentReadingProof] = useState<CurrentReadingProof | null>(null);
  const [mode, setMode] = useState<Mode>("view");
  const [searchParams, setSearchParams] = useSearchParams();
  const documentVisible = useDocumentVisible();
  const [widgetCount, setWidgetCount] = useState(0);
  const [missingSources, setMissingSources] = useState<string[]>([]);
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null);
  const [calcFieldsForSources, setCalcFieldsForSources] = useState<CalcField[]>([]);
  const autoRefreshIntervalMs = 5000;
  const janelasPessoais = useWidgetWindows(decodedWorkspaceId, decodedMachineId);

  const { state, reload, lastSuccessAt, lastError } = useAsync(
    `workspaces/${decodedWorkspaceId}/machines/${decodedMachineId}`,
    (signal) => getMachineDashboard(decodedWorkspaceId, decodedMachineId, signal),
  );

  const data = state.data;
  useEffect(() => {
    window.addEventListener("ghm:devices-updated", reload);
    return () => window.removeEventListener("ghm:devices-updated", reload);
  }, [reload]);
  const lastSyncedAt = useObservationClock(data !== null, lastSuccessAt);
  // Versão que escopa o layout/config do operador. Deriva da ESTRUTURA DE
  // SINAIS (portas × sinais), não do `configHashHex` inteiro: mudanças
  // cosméticas de config (firmware/cadência) mantêm o layout; só uma mudança
  // real de sinais recompõe o painel. Corrige a perda de tamanho no reload
  // sem reintroduzir widgets fantasma (ver deriveSignalStructureVersion).
  const dashboardConfigVersion = useMemo(
    () => (data ? deriveSignalStructureVersion(data.dashboards) : null),
    [data],
  );
  const starterSignals = useMemo(
    () =>
      data?.dashboards.flatMap((dashboard, slotIndex) =>
        buildStarterSignalsForDashboard(dashboard, slotIndex),
      ) ?? [],
    [data],
  );
  const starterSignalSources = useMemo(
    () =>
      starterSignals.map(
        (signal) => `G${signal.slotIndex}_P${signal.portIndex}_${signal.signal}`,
      ),
    [starterSignals],
  );
  const dashboardWidgets = useDashboardWidgets({
    workspaceId: decodedWorkspaceId,
    machineId: decodedMachineId,
    configVersion: dashboardConfigVersion,
    enabled: data !== null,
    userId: DEFAULT_OPERATOR_USER_ID,
    validSources: starterSignalSources,
  });
  // A preferência pode criar cópias dos cards (ex.: layout 24h, operador 7d).
  // Aplicá-la direto no JSX recria o array a cada render; WidgetPanel avisa
  // as fontes faltantes ao pai, que renderiza e cria outro array sem fim.
  // Esse ciclo de efeitos também impede o Router de concluir a navegação.
  const applyPersonalWindows = janelasPessoais.aplicar;
  const displayedWidgets = useMemo(
    () => applyPersonalWindows(dashboardWidgets.savedWidgets),
    [dashboardWidgets.savedWidgets, applyPersonalWindows],
  );
  const signalConfigsState = useSignalConfigs({
    enabled: data !== null,
    workspaceId: decodedWorkspaceId,
    machineId: decodedMachineId,
    configVersion: dashboardConfigVersion,
    userId: DEFAULT_OPERATOR_USER_ID,
  });
  const confirmedLayout = dashboardWidgets.remoteConfirmation;
  const confirmedSignals = signalConfigsState.remoteConfirmation;
  // Uma confirmacao do filho so vale para EXATAMENTE os inputs renderizados.
  // Ao trocar snapshot/config/prefs, um true do frame anterior nao certifica
  // o novo contexto antes de o WidgetPanel o resolver e montar.
  const performanceContext = useMemo(() => ({ data, displayedWidgets,
    signalConfigs: signalConfigsState.signalConfigs, confirmedLayout, confirmedSignals,
    preferencesConfirmed: janelasPessoais.preferencesConfirmed, lastSyncedAt }), [data, displayedWidgets,
    signalConfigsState.signalConfigs, confirmedLayout, confirmedSignals,
    janelasPessoais.preferencesConfirmed, lastSyncedAt]);
  const performanceContextConfirmed = performanceEnabled() && state.status === "success" &&
    lastError === null && lastSuccessAt !== null &&
    lastSyncedAt.getTime() - lastSuccessAt.getTime() <= 30_000 &&
    data?.machine.workspaceId === decodedWorkspaceId && data.machine.id === decodedMachineId &&
    !!confirmedLayout && !!confirmedSignals &&
    confirmedLayout.revision === confirmedSignals.revision && confirmedLayout.exists === confirmedSignals.exists &&
    janelasPessoais.preferencesConfirmed;
  useVerifiedPerformanceMilestone("dashboard_settled", performanceContextConfirmed, location.pathname);
  useVerifiedPerformanceMilestone("current_readings_commit",
    performanceContextConfirmed && currentReadingProof?.context === performanceContext &&
      currentReadingProof.ready, location.pathname);
  const configNotice = useConfigChangeNotice(
    decodedWorkspaceId,
    decodedMachineId,
    dashboardConfigVersion,
  );

  // Auto-refresh só dispara depois que a primeira resposta chegou. Sem essa
  // guarda, em uma stack lenta (worker subindo + dev-store frio) o intervalo
  // de 5s cancelava o fetch inicial antes dele resolver, prendendo a tela em
  // "Carregando workflow..." indefinidamente.
  //
  // O QUE NÃO É MAIS MOTIVO DE PAUSA: modal aberto. Durante um tempo o refresh
  // inteiro parava enquanto qualquer modal estivesse na tela, para o re-render
  // não reconstruir um dropdown nativo aberto embaixo do operador. O efeito
  // colateral era grave e o dono reproduziu: o editor de widget tem PRÉVIA AO
  // VIVO, e ela se alimenta de `latestPorts` — que vem do refetch. Com o
  // refetch parado o valor envelhecia sozinho, `isAvailable` caía, e a prévia
  // passava a exibir "--", "Sem leitura recente" e "Sem telemetria fresca para
  // esta fonte" enquanto o dispositivo publicava normalmente. Ou seja: a tela
  // acusava o CAMPO de silêncio quando quem tinha parado de perguntar era o
  // app — exatamente a mentira sobre o dado que o produto recusa em todo o
  // resto.
  //
  // E a pausa não estava comprando o que dizia comprar. Medido no navegador,
  // com o refetch de volta e 12 recargas seguidas: os 21 nós `<option>` do
  // seletor de fontes são OS MESMOS nós do DOM antes e depois. A reconciliação
  // do React atualiza a opção no lugar em vez de recriá-la, e um popup nativo
  // fica ancorado no nó — quem não é substituído não fecha. O tremor de layout
  // que motivou a pausa também não existe mais desde a malha: a geometria do
  // card vem das células, não da largura do texto, então valor novo não
  // redimensiona nada. O que sobrava era o custo.
  //
  // Se um dia o dropdown voltar a fechar sozinho, o alvo é a estabilidade das
  // `<option>` (chaves, ordem da lista) — não o refetch.

  // PUSH (SSE): a API avisa quando chega telemetria nova e o refetch acontece
  // na hora — a tela fica "ao vivo" sem depender do intervalo de polling.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const stream = useWorkspaceStream({
    workspaceId: decodedWorkspaceId,
    enabled: hasLoadedOnce(state),
    onChange: () => {
      reloadRef.current();
    },
  });

  useAutoRefresh({
    // `hasLoadedOnce` (não `status === "success"`): mantém o polling vivo
    // através de erros transitórios para a tela não congelar. Ver useAsync.ts.
    enabled: hasLoadedOnce(state),
    // Com o push conectado, o polling vira REDE DE SEGURANÇA esparsa (30s);
    // se o stream cair, volta ao ritmo de 5s até reconectar.
    intervalMs: stream.connected ? 30_000 : autoRefreshIntervalMs,
    onRefresh: reload,
  });

  const latestPorts = useMemo(
    () => data?.dashboards.flatMap((dashboard) => dashboard.latestPorts ?? []) ?? [],
    [data],
  );
  const configHashesByDevice = useMemo(
    () => new Map((data?.dashboards ?? []).map((dashboard) => [
      dashboard.device.id, dashboard.currentMeta?.configHashHex ?? null,
    ])),
    [data],
  );

  // Mapa slotIndex → runtime (online + janela de frescor). O slotIndex segue
  // a ordem de `data.dashboards` (mesma usada por buildDevMachine), então casa
  // com o `address.slotIndex` dos widgets. Alimenta o modelo de qualidade (P0):
  // sem isto o widget não diferencia "aguardando" de "offline", e a janela de
  // frescor vem da cadência declarada pelo device no /meta (P0.1b).
  const slotRuntime = useMemo<Map<number, SlotRuntime>>(
    () =>
      new Map(
        (data?.dashboards ?? []).map((dashboard, slotIndex) => {
          const parsed = parseMetaJson(dashboard.currentMeta);
          return [
            slotIndex,
            {
              // Frescor temporal; falha da plataforma e sinalizada separadamente.
              online: isDeviceOnlineNow(dashboard.device, undefined, lastSyncedAt),
              staleAfterMs: resolveStaleAfterMs(readTelemetryMs(parsed.data)),
            },
          ];
        }),
      ),
    [data, lastSyncedAt],
  );

  const automaticWidgets = useMemo(
    () =>
      data
        ? buildStarterWidgets(
            data.machine,
            latestPorts,
            signalConfigsState.index,
            starterSignals,
          )
        : [],
    [data, latestPorts, signalConfigsState.index, starterSignals],
  );

  // Mantem leitura de campos calculados apenas para compatibilidade com
  // widgets V0/V1 ja existentes. Criacao de formulas sai do fluxo principal
  // e voltara como area avancada.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = getMachineCalcStorageKey(decodedWorkspaceId, decodedMachineId);
    setCalcFieldsForSources(loadLocalCalcFields(window.localStorage, key, decodedMachineId));
  }, [decodedMachineId, decodedWorkspaceId]);

  // Lista de fontes reais que o operador pode usar como base de um widget novo.
  // Formulas V0/V1 ficam fora do fluxo principal ate voltarem como area avancada.
  const availableSources = useMemo((): AvailableSource[] => {
    // Com MAIS DE UM gateway, dois multimedidores produzem funções de mesmo
    // rótulo ("Tensao L1") e a lista fica com entradas indistinguíveis. O
    // prefixo só aparece nesse caso: com um device só ele seria ruído em
    // todas as linhas.
    const needsDevicePrefix = (data?.dashboards.length ?? 0) > 1;
    return (data?.dashboards ?? []).flatMap(
      (dashboard, slotIndex) => {
        const parsed = parseMetaJson(dashboard.currentMeta);
        const devicePrefix = needsDevicePrefix
          ? getDeviceDisplayName(dashboard.device, parsed.data)
          : null;
        const functions = sortMetaFunctionsByPort(getFunctions(parsed.data));
        return functions.flatMap((fn): AvailableSource[] => {
          if (typeof fn.p !== "number") return [];
          const baseTag = `G${slotIndex}_P${fn.p}`;
          const mode = normalizeMode(fn.m);
          // Lista declarada em metaJson.functions[].values cobre sinais que
          // o firmware publica. Quando ausente, infere pelo modo.
          const values = fn.values && !Array.isArray(fn.values) ? fn.values : null;
          // O meta é a fonte de verdade sobre O QUE existe; `isOperatorSelectableSignal`
          // decide o que é um SINAL PARA O OPERADOR. Sem esse filtro, o `s` de
          // AIN/MB — que é diagnóstico multi-estado — entrava na lista e podia
          // virar LED/disponibilidade, mostrando timeout de Modbus como
          // "Desligado". O fallback por modo passa pelo mesmo filtro para não
          // existirem duas regras que possam divergir.
          const signalsOf: Array<"s" | "acc" | "v"> = (
            values
              ? (["s", "acc", "v"] as const).filter((sig) => values[sig] !== undefined)
              : inferSignalsFromMode(mode)
          ).filter((sig) => isOperatorSelectableSignal(mode, sig));
          const baseLabel = typeof fn.label === "string" && fn.label.trim()
            ? fn.label.trim()
            : `Entrada ${fn.p + 1}`;
          return signalsOf.map((signal) => {
            const meta = getSignalMetadata(fn, signal);
            const valueLabel = meta.label ?? defaultSignalLabel(signal);
            const fullLabel = joinFunctionAndValueLabel(baseLabel, valueLabel);
            return {
              source: `${baseTag}_${signal}`,
              label: devicePrefix ? `${devicePrefix} · ${fullLabel}` : fullLabel,
              shortLabel: devicePrefix
                ? `${devicePrefix} · ${baseLabel}`
                : baseLabel,
              kind: "signal",
              signal,
              // A semântica declarada é o que a matriz de compatibilidade usa.
              // Aqui o modo é conhecido, então dá para resolver com precisão —
              // ao contrário do modo edição, que deduz da tag salva.
              signalKind: resolveSignalKind(mode, signal, meta),
              suggestedUnit: meta.unit,
              suggestedMin: meta.min,
              suggestedMax: meta.max,
              suggestedDecimals: meta.decimals,
              mode,
            };
          });
        });
      },
    );
  }, [data]);

  // Legendas curtas para comparativos. O seletor continua detalhado
  // ("Tensao L1 — Leitura"), mas dentro do card basta o nome da grandeza
  // publicado no meta. Sem isto, fontes extras sem SignalConfig caiam no
  // fallback tecnico "Entrada N — Velocidade".
  const availableSourceLabels = useMemo(
    () =>
      Object.fromEntries(
        availableSources.map((source) => [
          source.source,
          source.shortLabel ?? source.label,
        ]),
      ),
    [availableSources],
  );
  const availableSourceKinds = useMemo(
    () =>
      Object.fromEntries(
        availableSources.flatMap((source) =>
          source.signalKind ? [[source.source, source.signalKind] as const] : [],
        ),
      ),
    [availableSources],
  );
  // O MODO (DI/CT/PM/HSC/AIN/MB) nao viaja no widget, mas o papel do sinal
  // depende dele. Sem este mapa, salvar um card sobre um HSC ou um PM
  // rebaixava o `role` de "direction"/"flow" para "state"/"speed".
  // Nome DECLARADO por fonte. E o que devolve a identidade a um card cujo
  // titulo e automatico — ver `WidgetSettings.renamedByOperator`.
  const availableSourceNames = useMemo(
    () =>
      Object.fromEntries(availableSources.map((source) => [source.source, source.label])),
    [availableSources],
  );
  const availableSourceUnits = useMemo(
    () => Object.fromEntries(availableSources.map((source) => [source.source, source.suggestedUnit])),
    [availableSources],
  );
  const availableSourceModes = useMemo(
    () =>
      Object.fromEntries(
        availableSources.flatMap((source) =>
          source.mode ? [[source.source, source.mode] as const] : [],
        ),
      ),
    [availableSources],
  );

  const {
    workspaces: workflowsConhecidos,
    carregando: carregandoSetores,
    erro: erroDosSetores,
    recarregar: recarregarSetores,
    fonteEhServidor,
    renomear,
  } = useWorkspaces();

  // O setor da URL está disponível para ESTA SESSÃO?
  //
  // Enquanto carrega, assume que sim: piscar "nao existe" para um setor
  // legitimo e pior do que esperar. Quando a lista chega e o setor nao esta
  // nela, não sabemos se inexiste ou pertence a outro escopo. As rotas com
  // guarda respondem 404 nos dois casos — `/devices`, `/stream`, o
  // layout — e a tela ficava meio montada, com widgets de cache e botoes que
  // pareciam quebrados. O beco era completo: sem forma de sair pela interface.
  const setorConhecido = setorEhConhecido({
    fonteEhServidor,
    carregando: carregandoSetores,
    conhecidos: workflowsConhecidos.map((w) => w.id),
    alvo: decodedWorkspaceId,
  });
  const workflowName = useMemo(
    () =>
      workflowsConhecidos.find((workflow) => workflow.id === decodedWorkspaceId)?.name ?? null,
    [workflowsConhecidos, decodedWorkspaceId],
  );

  // A URL e a FONTE DA VERDADE deste modal, e nao um `useState` que um efeito
  // sincroniza com ela.
  //
  // Antes existiam os dois: o efeito via `?addDevice=1`, ligava o estado e
  // apagava a query em seguida. Funcionava, mas com estado duplicado — a
  // pergunta "o modal esta aberto?" tinha duas respostas possiveis, e mante-las
  // de acordo dependia de um efeito rodar na ordem certa.
  //
  // Derivar da query tira o segundo lugar de onde a resposta pode sair, e a URL
  // do modal aberto passa a ser compartilhavel. E o mesmo padrao que `?novo=1`
  // usa na lista de setores.
  const addDeviceOpen = searchParams.get("addDevice") === "1";
  const setAddDeviceOpen = useCallback(
    (aberto: boolean) => {
      const next = new URLSearchParams(searchParams);
      if (aberto) next.set("addDevice", "1");
      else next.delete("addDevice");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // body[data-mode] habilita o outline de edição nos widgets via CSS.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.dataset.mode = mode;
    return () => {
      delete document.body.dataset.mode;
    };
  }, [mode]);

  if (erroDosSetores && !workflowName) {
    return <div className="content"><ErrorState title="Não foi possível carregar os workflows"
      message="Falha de conexão com o servidor. Isso não significa que o workflow foi removido."
      onRetry={() => { recarregarSetores(); reload(); }} /></div>;
  }

  if (!setorConhecido || (state.status === "error" && !data && state.error instanceof ApiError && state.error.isNotFound)) {
    return (
      <div className="content">
        <ErrorState
          title="Workflow indisponível nesta sessão"
          message={`O workflow "${decodedWorkspaceId}" não está disponível para sua sessão. ` +
            "Confira se está usando a conta e a organização corretas. Abra um dos workflows disponíveis " +
            "ou peça ao administrador para verificar seu acesso."}
          onRetry={() => { recarregarSetores(); reload(); }}
          retryLabel="Verificar acesso novamente"
        />
        <div className="state__actions" style={{ marginTop: 12 }}>
          <Link className="btn ghost" to="/workflows">Ver workflows disponíveis</Link>
        </div>
      </div>
    );
  }

  // O primeiro commit precede o efeito que inicia a consulta. Idle ainda
  // nao e evidencia de ausencia de dados nem falha de acesso ao workflow.
  if ((state.status === "idle" || state.status === "loading") && !data) {
    return (
      <div className="content">
        <LoadingState label="Carregando workflow..." />
      </div>
    );
  }

  if (state.status === "error" && !data) {
    return (
      <div className="content">
        <ErrorState error={state.error} onRetry={reload} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="content">
        <ErrorState
          title="Sem dados do workflow"
          message="Nao foi possivel montar o workflow a partir dos devices associados."
          onRetry={reload}
        />
      </div>
    );
  }

  // Operador nao entra em modo de edicao. E UM ponto de corte, nao doze:
  // praticamente toda afordancia de escrita desta pagina ja pendurava em
  // `isEdit`, entao negar o modo apaga o conjunto inteiro de uma vez.
  //
  // A checagem e dupla de proposito. Esconder o botao (abaixo) resolve o caso
  // normal; anular o modo aqui cobre o estado que sobra quando a pessoa ja
  // estava editando e a sessao trocou — o painel volta para leitura sem
  // precisar de remount.
  const isEdit = mode === "edit" && permissoes.editarProprioPainel;
  const displayName = workflowName ?? data.machine.name;
  const gatewayCount = data.machine.slots.length;
  const channelCount = data.channelCount;
  const hasGateways = gatewayCount > 0;
  const onlineGatewayCount = data.dashboards.filter(
    // Idade da leitura continua avancando mesmo sem resposta da plataforma.
    (dashboard) => isDeviceOnlineNow(dashboard.device, undefined, lastSyncedAt),
  ).length;
  const workflowStatus = !hasGateways
    ? "waiting"
    : onlineGatewayCount > 0
      ? "online"
      : "offline";
  const workflowStatusBadge =
    workflowStatus === "online" ? "ok" : workflowStatus === "offline" ? "danger" : "muted";
  const workflowStatusLabel =
    workflowStatus === "online" ? "ONLINE" : workflowStatus === "offline" ? "OFFLINE" : "AGUARDANDO";
  const syncUnavailable = lastError !== null || (lastSuccessAt !== null &&
    lastSyncedAt.getTime() - lastSuccessAt.getTime() > 30_000);

  const shiftView = parseShiftView(searchParams.get(SHIFT_VIEW_PARAM));
  const liveView = shiftView === "live";
  const renderHeader = (context: ReactNode, actions?: ReactNode) => <PageHeader
        crumbs={[
          { label: "Dashboard", to: "/workflows" },
          { label: "Workflows", to: "/workflows" },
          { label: displayName },
        ]}
        title={displayName}
        settingsTo={`/workflows/${encodeURIComponent(decodedWorkspaceId)}/configuracoes`}
        settingsLabel="Turnos e indicadores"
        actions={actions}
        titleBadge={
          <>
            {permissoes.gerenciarWorkspaces ? <RenameButton key={decodedWorkspaceId} label="Renomear workflow"
              value={displayName} help="O endereço e os dispositivos deste workflow não mudam."
              onSave={(name) => renomear(decodedWorkspaceId, name)} /> : null}
            <span className={`badge ${syncUnavailable ? "muted" : workflowStatusBadge}`}>
              <span className={!syncUnavailable && workflowStatus === "online" ? "led-anim" : "dot"} />
              {syncUnavailable ? "SEM ATUALIZAÇÃO" : workflowStatusLabel}
            </span>
            <span className="badge muted">{channelCount} sinal(is)</span>
          </>
        }
        meta={
          <>
            <span className="item item--gateway">
              <IconBroadcast size={12} /> Gateway{" "}
              <b>
                {syncUnavailable ? `-- de ${gatewayCount} ativo(s)` : `${onlineGatewayCount} de ${gatewayCount} ativo(s)`}
              </b>
            </span>
            <span className="item">
              <IconClock size={12} /> Último sinal{" "}
              <b>{formatRelative(data.lastSeenAt, lastSyncedAt)}</b>
            </span>
            {stream.connected && !syncUnavailable ? (
              // Push SSE ativo: a tela atualiza no instante em que a
              // telemetria chega — sem depender do intervalo de polling.
              <span className="item" title="Atualização por push (SSE): dados chegam em tempo real">
                <span className="led-anim" /> <b>Ao vivo</b>
              </span>
            ) : (
              <span className="item">
                <IconRefresh size={12} /> Auto-refresh{" "}
                <b>{autoRefreshIntervalMs / 1000}s</b>
              </span>
            )}
          </>
        }
        mode={liveView && permissoes.editarProprioPainel ? mode : undefined}
        onModeChange={liveView && permissoes.editarProprioPainel ? setMode : undefined}
        context={context}
      />;

  return (
    <>
      <Suspense fallback={renderHeader(null)}><ShiftDashboard workflowId={decodedWorkspaceId} devices={data.dashboards}
        liveAvailable={!syncUnavailable} renderHeader={renderHeader} canEdit={permissoes.gerenciarWorkspaces} /></Suspense>

      {/* Turnos/Comparativo substituem as leituras ao vivo; ocultar preserva estado e modo de edicao. */}
      <div className="content" style={liveView ? undefined : { display: "none" }}>
        {syncUnavailable ? (
          <div className="inline-warning" role="status">
            Sem atualização da plataforma. Não é possível confirmar o estado atual dos dispositivos.
            {lastSuccessAt ? ` Última consulta bem-sucedida ${formatRelative(lastSuccessAt.toISOString(), lastSyncedAt)}.` : ""}
            <button type="button" className="btn ghost" onClick={reload}>Tentar novamente</button>
          </div>
        ) : null}
        {janelasPessoais.error ? <div className="inline-warning" role="alert">{janelasPessoais.error}</div> : null}
        {configNotice.changed ? (
          <div
            className="inline-warning"
            role="status"
            style={{
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <span>
              A configuração do gateway mudou. O painel foi recomposto a partir da
              nova configuração do hardware — widgets e ajustes da configuração
              anterior ficaram guardados.
            </span>
            <button type="button" className="btn sm" onClick={configNotice.dismiss}>
              Entendi
            </button>
          </div>
        ) : null}

        {!hasGateways ? (
          <div className="state">
            <h2 className="state__title">Workflow sem gateway</h2>
            <p className="state__message">
              Adicione um dispositivo GHM informando o ID dele. Após o claim, os sinais
              do <code className="mono">metaJson</code> retido aparecem automaticamente
              como widgets.
            </p>
            {permissoes.gerenciarDispositivos ? (
              <div className="state__actions">
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => setAddDeviceOpen(true)}
                >
                  <IconPlus size={12} /> Adicionar dispositivo
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {hasGateways ? (
          <section>
            <div className="section-h">
              <h2>
                Leituras ao vivo <span className="num">{widgetCount}</span>
              </h2>
              {isEdit ? (
                <div className="right">
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => setAddDeviceOpen(true)}
                    title="Associar mais um gateway ao workflow"
                  >
                    <IconPlus size={12} /> Adicionar dispositivo
                  </button>
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => setAddWidgetOpen(true)}
                    title="Criar um widget novo apontando para qualquer sinal ou cálculo"
                  >
                    <IconPlus size={12} /> Adicionar widget
                  </button>
                  {dashboardWidgets.savedWidgets.length > 0 ? (
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => {
                        if (
                          typeof window !== "undefined" &&
                          window.confirm(
                            "Resetar layout? Os widgets configurados serão apagados e a prévia automática volta.",
                          )
                        ) {
                          dashboardWidgets.reset();
                        }
                      }}
                      title="Apaga os widgets salvos e volta para a prévia automática"
                    >
                      Resetar layout
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div style={{ marginTop: 10 }}>
              {missingSources.length > 0 && isEdit ? (
                <div
                  className="inline-warning"
                  role="status"
                  style={{
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span>
                    <strong>{missingSources.length}</strong> sinal(is) publicado(s) pelo
                    gateway ainda não estão no painel:{" "}
                    <code className="mono">{missingSources.slice(0, 3).join(", ")}</code>
                    {missingSources.length > 3 ? "…" : ""}
                  </span>
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => {
                      const added = appendMissingStarterWidgets(
                        starterSignals,
                        availableSources,
                        dashboardWidgets.savedWidgets,
                        data.machine.id,
                      );
                      if (added.length === 0) return;
                      for (const widget of added) dashboardWidgets.addWidget(widget);
                    }}
                  >
                    <IconPlus size={12} /> Adicionar widgets faltantes
                  </button>
                </div>
              ) : null}
              <WidgetPanel
                active={liveView && documentVisible}
                performanceContext={performanceContextConfirmed ? performanceContext : undefined}
                onCurrentReadingReadyChange={setCurrentReadingProof}
                configHashesByDevice={configHashesByDevice}
                machine={data.machine}
                latestPorts={latestPorts}
                slotRuntime={slotRuntime}
                referenceNow={lastSyncedAt}
                starterSignals={starterSignals}
                savedWidgets={displayedWidgets}
                signalConfigs={signalConfigsState.signalConfigs}
                sourceLabels={availableSourceLabels}
                sourceNames={availableSourceNames}
                sourceUnits={availableSourceUnits}
                sourceKinds={availableSourceKinds}
                readOnly={!isEdit}
                onCountChange={setWidgetCount}
                onMissingSourcesChange={setMissingSources}
                onEditWidget={setEditingWidget}
                onResizeWidget={(widgetId, cells) => {
                  // O card redimensionado pode ainda ser uma previa
                  // automatica; `automaticWidgets` promove o layout inteiro
                  // para salvo, senao o arrasto sumiria no proximo render.
                  const alvo = mesclarWidgets(
                    dashboardWidgets.savedWidgets,
                    automaticWidgets,
                  ).find((widget) => widget.id === widgetId);
                  if (!alvo) return;
                  dashboardWidgets.upsertWidget(
                    {
                      ...alvo,
                      layout: { ...alvo.layout, w: cells.w, h: cells.h, sizeExplicit: true },
                    },
                    automaticWidgets,
                  );
                }}
                onMoveWidget={(posicoes) => {
                  // Grava TODOS de uma vez: o primeiro movimento congela o
                  // painel, senao os cards que ainda fluem se reacomodam em
                  // volta do que foi movido e saltam sozinhos.
                  //
                  // `mesclarWidgets` nao e detalhe: sem ele o universo tinha o
                  // mesmo id duas vezes — a versao configurada e a previa
                  // automatica — e a segunda apagava a primeira. Um toque de
                  // seta devolvia titulo, unidade, decimais e regra de producao
                  // ao padrao, sem aviso nenhum.
                  const porId = new Map(posicoes.map((p) => [p.id, p]));
                  const proximos = mesclarWidgets(
                    dashboardWidgets.savedWidgets,
                    automaticWidgets,
                  ).flatMap((widget) => {
                    const pos = porId.get(widget.id);
                    return pos
                      ? [{ ...widget, layout: { ...widget.layout, x: pos.x, y: pos.y } }]
                      : [];
                  });
                  // UMA gravacao para o lote inteiro. Um `upsert` por card
                  // disparava um PUT por card, todos concorrentes e sem ordem
                  // garantida no servidor: o ultimo a chegar vencia, e podia ser
                  // um estado parcial do arrasto.
                  dashboardWidgets.upsertWidgets(proximos, automaticWidgets);
                }}
                onGenerateChart={(origin) => {
                  // O gráfico nasce de um widget numérico (menu "…" em
                  // Visualização). Promove a prévia automática se ainda não
                  // houver layout salvo e adiciona o gráfico ao final.
                  dashboardWidgets.upsertWidget(
                    deriveChartWidget(origin, {
                      id: origin.id,
                      order: origin.layout.order,
                    }),
                    automaticWidgets,
                  );
                }}
                onHideChart={(target) => {
                  // Volta ao card de origem. `revertChartWidget` devolve o
                  // widget intacto se nao houver marca de origem, entao o pior
                  // caso e um clique sem efeito — nunca um card destruido.
                  dashboardWidgets.upsertWidget(
                    revertChartWidget(target),
                    automaticWidgets,
                  );
                }}
                onChangeWidgetWindow={(target, window) => {
                  // Seletor de período NO CARD: é exploração individual, e por
                  // isso vai para a preferência da PESSOA, não para o layout do
                  // painel — que é compartilhado por todo o turno. O hook de
                  // dados mantém a série anterior visível (`reloading`) até a
                  // nova janela chegar.
                  if (janelasPessoais.definir(target.id, window)) return;
                  // Bancada: sem identidade não há preferência pessoal, e há
                  // uma pessoa só. Grava no layout, como sempre foi.
                  dashboardWidgets.upsertWidget(
                    applyWidgetWindow(target, window),
                    automaticWidgets,
                  );
                }}
              />
            </div>
          </section>
        ) : null}

      </div>

      {addDeviceOpen ? (
        <AddDeviceModal
          workspaceId={decodedWorkspaceId}
          onClose={() => setAddDeviceOpen(false)}
          onClaimed={() => {
            setAddDeviceOpen(false);
            reload();
            // Avisa o Sidebar (e qualquer outra tela) que a lista de
            // devices do workspace mudou — sem isto a barra lateral só
            // recarregava os gateways num F5 ou troca de workflow.
            if (typeof window !== "undefined") {
              window.dispatchEvent(new Event("ghm:devices-updated"));
            }
          }}
        />
      ) : null}

      {addWidgetOpen ? (
        <WidgetEditorModal
          sources={availableSources}
          knownSections={knownSectionNames(dashboardWidgets.savedWidgets)}
          machineId={data.machine.id}
          nextOrder={
            dashboardWidgets.savedWidgets.length > 0
              ? Math.max(...dashboardWidgets.savedWidgets.map((w) => w.layout.order)) + 1
              : widgetCount
          }
          resolveLiveValue={(source) =>
            resolveWidgetValue(
              buildLiveValueWidget(source, data.machine.id),
              data.machine,
              latestPorts,
              calcFieldsForSources,
            )
          }
          onClose={() => setAddWidgetOpen(false)}
          onSave={(widget) => {
            dashboardWidgets.addWidget(widget, automaticWidgets);
            // Para sinais reais, replica as preferências também no
            // SignalConfig — assim outros widgets do mesmo G-tag (ou um
            // futuro "Resetar layout") pegam o que o operador definiu.
            // Widgets DERIVADOS (disponibilidade/utilização) NÃO entram: a
            // unidade "%" deles é da visão, não do sinal, e sobrescreveria a
            // unidade física (ex.: metros) dos demais cards da mesma porta.
            if (widget.source.startsWith("G") && widgetDefinesSignalIdentity(widget)) {
              signalConfigsState.upsert(
                // A config ANTERIOR entra porque carrega um fato que o widget
                // nao tem: se o nome do sinal ja era escolha de alguem.
                widgetToSignalConfig(
                  widget,
                  availableSourceModes[widget.source],
                  signalConfigsState.index.get(widget.source),
                ),
              );
            }
            setAddWidgetOpen(false);
          }}
        />
      ) : null}

      {editingWidget ? (
        <WidgetEditorModal
          sources={availableSources}
          knownSections={knownSectionNames(dashboardWidgets.savedWidgets)}
          existingWidget={editingWidget}
          machineId={data.machine.id}
          resolveLiveValue={(source) =>
            resolveWidgetValue(
              { ...editingWidget, source },
              data.machine,
              latestPorts,
              calcFieldsForSources,
            )
          }
          onClose={() => setEditingWidget(null)}
          onSave={(next) => {
            // Promove starter para savedWidgets se ainda não estiver, e
            // mantém o SignalConfig em sincronia para G-sources. Widgets
            // derivados (disponibilidade/utilização) ficam de fora para não
            // gravar o "%" da visão como unidade do sinal físico.
            dashboardWidgets.upsertWidget(next, automaticWidgets);
            if (next.source.startsWith("G") && widgetDefinesSignalIdentity(next)) {
              signalConfigsState.upsert(
                widgetToSignalConfig(
                  next,
                  availableSourceModes[next.source],
                  signalConfigsState.index.get(next.source),
                ),
              );
            }
            setEditingWidget(null);
          }}
          onRemove={() => {
            // Remove o widget salvo (se houver); se for um starter, basta
            // ocultar pela SignalConfig (mais consistente que "remover"
            // algo que não está salvo).
            //
            // A busca é por ID, nunca por source: com dois cards da mesma
            // fonte (ex.: gauge + alarme de velocidade), buscar por source
            // removia o PRIMEIRO widget da fonte em vez do que estava aberto
            // no editor — apagando o card errado do operador.
            const saved = dashboardWidgets.savedWidgets.find(
              (w) => w.id === editingWidget.id,
            );
            if (saved) dashboardWidgets.removeWidget(saved.id);
            else if (editingWidget.source.startsWith("G")) {
              const current = signalConfigsState.index.get(editingWidget.source);
              if (current) {
                signalConfigsState.upsert({ ...current, hidden: true });
              }
            }
            setEditingWidget(null);
          }}
          onResetSignalConfig={
            editingWidget.source.startsWith("G") &&
            signalConfigsState.index.has(editingWidget.source)
              ? () => {
                  signalConfigsState.remove(editingWidget.source);
                  setEditingWidget(null);
                }
              : undefined
          }
        />
      ) : null}
    </>
  );
}

/**
 * Constrói widgets default a partir dos starters que ainda não estão
 * cobertos pelos saved widgets. Usado pelo botão "Adicionar widgets
 * faltantes" no banner que aparece quando o gateway publica um sinal
 * novo após o operador ter salvo um layout.
 */
function appendMissingStarterWidgets(
  starters: StarterSignalDefinition[],
  available: AvailableSource[],
  saved: import("@/features/widgets/types").DashboardWidget[],
  machineId: string,
): import("@/features/widgets/types").DashboardWidget[] {
  const savedSources = new Set(saved.map((w) => w.source));
  const lookup = new Map(available.map((a) => [a.source, a]));
  const baseOrder =
    saved.length > 0
      ? Math.max(...saved.map((w) => w.layout.order)) + 1
      : 0;

  return starters
    .map((starter, idx) => ({ starter, idx }))
    .filter(({ starter }) => {
      const source = `G${starter.slotIndex}_P${starter.portIndex}_${starter.signal}`;
      return !savedSources.has(source);
    })
    .map<import("@/features/widgets/types").DashboardWidget>(({ starter, idx }) => {
      const source = `G${starter.slotIndex}_P${starter.portIndex}_${starter.signal}`;
      const meta = lookup.get(source);
      const widgetType =
        starter.signal === "s" ? "led" : starter.signal === "v" ? "gauge" : "number";
      return {
        id: `widget-${source}-${Date.now().toString(36)}-${idx}`,
        machineId,
        title: starter.label ?? meta?.label ?? source,
        source,
        widgetType,
        settings: {
          signalKind: starter.kind ?? meta?.signalKind,
          unit: starter.unit ?? meta?.suggestedUnit ?? undefined,
        },
        layout: {
          size: widgetType === "gauge" ? "lg" : "md",
          order: baseOrder + idx,
        },
      };
    });
}

/**
 * Esqueleto mínimo de `DashboardWidget` usado pelo `resolveLiveValue`
 * dentro do modal de criar widget (quando ainda não há widget completo).
 * O `resolveWidgetValue` só precisa de `source` e `settings.thresholds`
 * para resolver — passamos um settings vazio para evitar
 * `Cannot read properties of undefined`.
 */
function buildLiveValueWidget(source: string, machineId: string): DashboardWidget {
  return {
    id: "__live__",
    machineId,
    title: source,
    source,
    widgetType: "number",
    settings: {},
    layout: { size: "md", order: 0 },
  };
}

/**
 * A senha, mostrada UMA vez.
 *
 * Nao ha rota que a devolva depois: o broker guarda so o hash. Fingir que da
 * para recuperar exigiria guardar a senha em texto claro em algum lugar — que
 * e exatamente o que nao se deve fazer com credencial de equipamento. Perdeu,
 * gera outra: e uma operacao de segundos.
 *
 * Os tres campos aparecem juntos porque os tres precisam ser digitados no
 * configurador na mesma tela. Mostrar so a senha obrigaria a pessoa a procurar
 * host e porta em outro lugar, e o host errado e uma das formas de a placa
 * "conectar e nao entregar nada".
 */
function CredencialGerada({ credencial }: { credencial: CredencialDeDispositivo }) {
  const [copiado, setCopiado] = useState(false);

  return (
    <div className="cred-box">
      <div className="cred-box__head">
        <strong>
          {credencial.jaExistia ? "Senha trocada" : "Credencial criada"}
        </strong>
        <span className="muted">aparece uma única vez</span>
      </div>

      {credencial.jaExistia ? (
        <p className="inline-warning" role="status" style={{ margin: 0 }}>
          Esta placa já tinha credencial. A senha antiga <b>deixou de valer</b> — o
          dispositivo só volta a publicar depois de ser regravado com a senha abaixo.
        </p>
      ) : null}

      <dl className="cred-box__grid">
        <dt>Servidor</dt>
        <dd className="mono">{window.location.hostname}</dd>
        <dt>Porta</dt>
        <dd className="mono">8883 · TLS ligado</dd>
        <dt>Usuário</dt>
        <dd className="mono">deixe vazio no configurador</dd>
        <dt>Senha</dt>
        <dd className="mono cred-box__senha">{credencial.senha}</dd>
      </dl>

      <button
        type="button"
        className="btn sm"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(credencial.senha)
            .then(() => setCopiado(true))
            // Área de transferência bloqueada (contexto inseguro, permissão
            // negada) não pode virar erro: a senha está na tela, dá para
            // digitar.
            .catch(() => setCopiado(false));
        }}
      >
        {copiado ? "Senha copiada" : "Copiar senha"}
      </button>

      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        Grave a placa com esses dados. Ela vai publicar, aparecer em
        &quot;descobertos&quot;, e aí você a associa a este workflow pela aba ao lado.
      </p>
    </div>
  );
}

function inferSignalsFromMode(mode: string): Array<"s" | "acc" | "v"> {
  if (mode === "DI" || mode === "LATCH" || mode === "RST") return ["s"];
  if (mode === "CT") return ["s", "acc", "v"];
  if (mode === "PM" || mode === "HSC") return ["s", "acc", "v"];
  // AIN (leitura analógica): valor instantâneo + total quando o bloco
  // totaliza. O `s` do payload é o status NE43, não um sinal operacional.
  if (mode === "AIN") return ["v", "acc"];
  // MB expoe a grandeza lida e, quando configurado no firmware, o total.
  // O `s` e diagnostico de comunicacao, nao um estado binario operacional.
  if (mode === "MB") return ["v", "acc"];
  return ["s"];
}

function defaultSignalLabel(signal: "s" | "acc" | "v"): string {
  if (signal === "s") return "Estado";
  if (signal === "acc") return "Total";
  return "Taxa";
}

interface AddDeviceModalProps {
  workspaceId: string;
  onClose: () => void;
  onClaimed: () => void;
}

type ClaimState =
  | { status: "idle"; message: null }
  | { status: "submitting"; message: null }
  | { status: "success"; message: string }
  | { status: "error"; message: string };

/**
 * DOIS momentos, nao um.
 *
 * A ordem mudou quando o broker passou a exigir autenticacao, e a mudanca e
 * contraintuitiva para quem conhecia o fluxo antigo: a credencial vem ANTES de
 * o dispositivo existir para o sistema. Sem credencial a placa nao conecta;
 * sem conectar ela nunca publica; sem publicar ela nunca aparece em
 * "descobertos". "Ligue a placa e ela aparece" deixou de valer.
 *
 * Por isso a tela pergunta primeiro em que ponto a pessoa esta, em vez de
 * oferecer um formulario so que funciona para metade dos casos.
 */
type ModoAdicao = "credencial" | "associar";

function AddDeviceModal({ workspaceId, onClose, onClaimed }: AddDeviceModalProps) {
  const [modo, setModo] = useState<ModoAdicao>("credencial");
  const [mqttId, setMqttId] = useState("");
  const [state, setState] = useState<ClaimState>({ status: "idle", message: null });
  const [credencial, setCredencial] = useState<CredencialDeDispositivo | null>(null);
  const modalRef = useModalA11y({ open: true, onClose });

  async function provisionar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const id = mqttId.trim();
    if (!id) {
      setState({ status: "error", message: "Informe o ID do dispositivo." });
      return;
    }

    setState({ status: "submitting", message: null });
    setCredencial(null);
    try {
      setCredencial(await provisionarCredencial(workspaceId, id));
      setState({ status: "idle", message: null });
    } catch (error) {
      setState({
        status: "error",
        message:
          error instanceof ApiError && error.status === 501
            ? "Esta instalacao nao tem acesso administrativo ao broker. A credencial precisa ser criada no servidor."
            : error instanceof Error
              ? error.message
              : "Falha ao gerar a credencial.",
      });
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedMqttId = mqttId.trim();
    if (!trimmedMqttId) {
      setState({ status: "error", message: "Informe o ID do dispositivo." });
      return;
    }

    setState({ status: "submitting", message: null });
    try {
      const result = await claimDevice({
        mqttId: trimmedMqttId,
        workspaceId,
        claimedByUserId: "local-user",
      });
      const claimedDevice =
        (result as { workspaceId?: string }).workspaceId ?? workspaceId;
      setState({
        status: "success",
        message: `Dispositivo associado ao workflow ${claimedDevice}.`,
      });
      setTimeout(onClaimed, 350);
    } catch (error) {
      const message =
        error instanceof Error && error.message.includes("Failed to fetch")
          ? "Nao foi possivel conectar na API. Verifique se o backend esta rodando na porta 3001."
          : error instanceof Error
            ? error.message
            : "Falha ao associar dispositivo.";
      setState({ status: "error", message });
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-device-title"
        ref={modalRef}
        tabIndex={-1}
      >
        <header className="modal__header">
          <div>
            <p className="page__eyebrow">Onboarding</p>
            <h2 id="add-device-title">Adicionar dispositivo</h2>
          </div>
          <button
            type="button"
            className="btn ghost sm"
            onClick={onClose}
            aria-label="Fechar"
          >
            Fechar
          </button>
        </header>

        <div className="seg" role="tablist" aria-label="Etapa da instalação">
          <button
            type="button"
            role="tab"
            aria-selected={modo === "credencial"}
            className={modo === "credencial" ? "on" : ""}
            onClick={() => {
              setModo("credencial");
              setState({ status: "idle", message: null });
            }}
          >
            Placa nova
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={modo === "associar"}
            className={modo === "associar" ? "on" : ""}
            onClick={() => {
              setModo("associar");
              setState({ status: "idle", message: null });
              setCredencial(null);
            }}
          >
            Já está publicando
          </button>
        </div>

        <form
          className="modal__body"
          onSubmit={modo === "credencial" ? provisionar : handleSubmit}
        >
          <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
            {modo === "credencial" ? (
              <>
                A placa precisa de uma credencial <b>antes</b> de conseguir publicar.
                Conecte-a no configurador por USB, copie o{" "}
                <b>Código MQTT</b> que aparece lá e cole aqui.
              </>
            ) : (
              <>
                Informe apenas o ID do dispositivo GHM. Após o claim, o sistema puxa o
                pacote <code className="mono">/meta</code> retido no broker e o
                dispositivo aparece como um novo gateway.
              </>
            )}
          </p>

          <label className="field">
            <span className="field__label">ID do dispositivo</span>
            <input
              className="input field__control mono"
              value={mqttId}
              onChange={(event) => setMqttId(event.target.value)}
              placeholder="ghm-841fe81b6e7c"
              autoFocus
            />
          </label>

          {state.message ? (
            <div
              className={state.status === "error" ? "inline-warning" : "inline-success"}
              role="status"
            >
              {state.message}
            </div>
          ) : null}

          {credencial ? <CredencialGerada credencial={credencial} /> : null}

          <div className="modal__actions">
            <button type="button" className="btn ghost" onClick={onClose}>
              {credencial ? "Fechar" : "Cancelar"}
            </button>
            <button
              type="submit"
              className="btn primary"
              disabled={state.status === "submitting"}
            >
              {state.status === "submitting"
                ? modo === "credencial"
                  ? "Gerando..."
                  : "Associando..."
                : modo === "credencial"
                  ? credencial
                    ? "Gerar outra"
                    : "Gerar credencial"
                  : "Associar dispositivo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
